import React, { useContext, useMemo, useState } from 'react';
import { InfoIcon } from '../../svg';
import Model from '../common/models/Model';
import { BrandMemberRole } from '../../types/enums';
import { getInfoText } from '../../utils/brandUtils';
import { Button } from '../common/Button';
import Input from '../common/Input';
import useI18n from '../../hooks/useI18n';
import { toast } from 'react-toastify';
import { UserContext } from '@/context/UserContext';

interface AddUserModalProps {
  isModalOpen: boolean;
  handleModal: () => void;
  onAddUser: (user: { name: string; email: string; role: string }) => void;
}

const AddUserModal: React.FC<AddUserModalProps> = ({
  isModalOpen,
  handleModal,
  onAddUser,
}) => {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const userContext = useContext(UserContext);
  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user } = userContext;

  const isManager = useMemo(
    () => user?.brandMemberRole === BrandMemberRole.Manager,
    [user?.brandMemberRole],
  );

  const validateForm = () => {
    let isValid = true;

    if (!name.trim()) {
      toast.error(t('errors.fullNameRequired'));
      isValid = false;
    } else if (!email.trim()) {
      toast.error(t('errors.emailRequired'));
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(t('errors.email'));
      isValid = false;
    } else if (!role) {
      toast.error(t('errors.roleRequired'));
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (validateForm()) {
      onAddUser({ name, email, role });
      handleModal();
      setName('');
      setEmail('');
      setRole('');
    }
  };

  const handleClose = () => {
    setName('');
    setEmail('');
    setRole('');
    handleModal();
  };

  return (
    <Model isOpen={isModalOpen}>
      <div className="md:w-[400px]">
        <h1 className="text-[24px] font-header text-[#212121]">
          {t('userManagement.addNewUser')}
        </h1>
        <Input
          required={true}
          type="text"
          placeholder={String(t('userManagement.fullName'))}
          className="my-[16px] font-content"
          value={name}
          onChange={(e) => setName(e.target.value)} // Mise à jour de l'état
        />
        <Input
          required={true}
          type="text"
          placeholder={String(t('userManagement.workEmail'))}
          className="mb-[16px] font-content"
          value={email}
          onChange={(e) => setEmail(e.target.value)} // Mise à jour de l'état
        />
        <select
          className="w-full cursor-pointer p-3 rounded-full border outline-none border-gray-400 mb-[16px]"
          value={role}
          onChange={(e) => setRole(e.target.value)} // Mise à jour de l'état
          required={true}
        >
          <option value="" disabled>
            {t('userManagement.selectRole')}
          </option>
          <option value={BrandMemberRole.User}>
            {t('userManagement.roles.user')}
          </option>
          <option value={BrandMemberRole.Manager}>
            {t('userManagement.roles.manager')}
          </option>
          <option value={BrandMemberRole.Admin} disabled={isManager}>
            {t('userManagement.roles.admin')}
          </option>
        </select>
        <div className="mt-2 flex justify-end items-center gap-2">
          <Button onClick={() => handleClose()} variant="outlined">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} type="submit">
            {t('userManagement.createUser')}
          </Button>
        </div>
        {role && (
          <div className="p-[24px] bg-[#f0f5ff] rounded-3xl mt-4">
            <div className="flex items-start">
              <div className="mr-2">
                <InfoIcon className="size-6 text-[#4F4949]" />
              </div>
              <div className="mt-[2px]">
                <h2 className="font-header text-lg font-[600] leading-none text-[#172b4d] mb-2">
                  {t(`userManagement.roles.${role.toLowerCase()}`)}
                </h2>
                <p className="font-content text-sm text-[#4F4949]">
                  {getInfoText(role)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Model>
  );
};

export default AddUserModal;
